import DepsService from 'services/dependencies';
import utils from 'utils/lsp-utils-service';
import _ from 'lodash';

const USER_CONTEXT = 'user';
const USER_APP_CONTEXT = 'app';

export const getUsers = (parameters) => {
  const { url } = DepsService.rest.user.users;
  const queryParams = { ...parameters };
  if (_.get(parameters, 'filters.mobilePhone')) {
    queryParams.filters.mobilePhone = parameters.filters.mobilePhone.replace(/[^\d]/g, ''); // todo is this necessary?
  }
  const urlParams = {};

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams).then((result) => {
    (result.content || []).forEach((user) => {
      user.name = `${user.contact.lastName} ${user.contact.firstName}`;
    });
    return result;
  });
};

export function downloadUsersWithGroups(fileType, pagingModel) {
  const { url } = DepsService.rest.user.users[fileType];
  const queryParams = {
    groupMember: true,
    'page-number': pagingModel.pageNumber,
    'page-size': pagingModel.pageSize,
    sortProperty: pagingModel.sortProperty,
    ascending: pagingModel.ascending,
    ...pagingModel.filters,
  };
  const urlParams = {};

  return DepsService.restSrv.downloadBlob(USER_CONTEXT, url, queryParams, urlParams);
}

export const getAvailableMembers = ({ userId, ...params }) => {
  const { url } = DepsService.rest.user.users.availableMembers;
  const queryParams = params;
  const urlParams = {
    userId,
  };

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams).then((result) => result);
};

export const getAvailableAdminMembers = (parameters) => {
  const { url } = DepsService.rest.user.users;
  const queryParams = parameters;
  const urlParams = {};

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams).then((result) => result);
};

const loadUserByParams = (url, urlParams) => {
  const queryParams = {};

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams).then((result) => result);
};

export const loadUserByLogin = (userLogin) => {
  const { url } = DepsService.rest.account.entryByLogin;
  const queryParams = { login: userLogin };

  return loadUserByParams(url, queryParams);
};

export const loadUserById = (id) => {
  const { url } = DepsService.rest.account.self.data;
  const queryParams = { id };

  return loadUserByParams(url, queryParams);
};

export const saveUserData = (data) => {
  const { url } = DepsService.rest.account.self.update;

  return DepsService.restSrv.update(USER_CONTEXT, url, data);
};

export const saveUserLocale = (id, locale) =>
  loadUserById(id).then((result) => {
    saveUserData({
      locale,
      firstName: result.contact.firstName,
      lastName: result.contact.lastName,
      email: result.contact.emailAddress,
      mobilePhone: result.contact.mobilePhone,
      contactPhone: result.contact.contactPhone,
      securityQuestions: result.securityQuestions,
      ownerUserId: result.ownerUserId,
    });
  });

export const getPasswordResetQuestions = (username) => {
  const { url } = DepsService.rest.user.securityQuestions;
  const queryParams = { username };

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams).then((result) => result);
};

export const forceResetAccountPassword = (username) => {
  const { url } = DepsService.rest.forcePasswordReset;
  const queryParams = { username };

  return DepsService.restSrv.create(USER_CONTEXT, url, queryParams);
};

export const resetAccountPassword = (username, questionsAndAnswers) => {
  const { url } = DepsService.rest.passwordReset;
  const { questions, answers } = questionsAndAnswers;
  const challenges = answers.reduce((reduced, item, key) => {
    reduced.push({
      questionId: questions[key].code,
      answer: answers[key],
    });
    return reduced;
  }, []);
  const queryParams = {
    username,
    challenges,
  };

  return DepsService.restSrv.create(USER_CONTEXT, url, queryParams);
};

export const fetchSelfRegistrationSettings = (retailerId) => {
  const { url } = DepsService.rest.user.retailerSettings.validate;
  const queryParams = {};
  const urlParams = { retailerId };

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams);
};

export const fetchTrustedDevices = () => {
  const { url } = DepsService.rest.auth.twoFactor.trustedDevicesList;
  const queryParams = {};
  const urlParams = {};

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams);
};

export const deleteTrustedDevice = (uuid) => {
  const { url } = DepsService.rest.auth.twoFactor.deleteDevice;
  const queryParams = { uuid };
  const urlParams = {};

  return DepsService.restSrv.remove(USER_CONTEXT, url, queryParams, urlParams);
};

export const saveSelfRegistrationSettings = (selfRegistrationEnabled, retailerId) => {
  const { url } = DepsService.rest.retailer.saveSubordinateData;
  const queryParams = { selfRegistrationEnabled };
  const urlParams = { retailerId };

  return DepsService.restSrv.update(USER_CONTEXT, url, queryParams, urlParams);
};

export const fetchUserSecurityQuestions = (userId) => {
  const { url } = DepsService.rest.challenges;
  const queryParams = {};
  const urlParams = { userId };

  return DepsService.restSrv.list(USER_APP_CONTEXT, url, queryParams, urlParams);
};

export const createUser = (user, locale, retailerDisplayNumber) => {
  const { url } = DepsService.rest.account.create;

  const queryParams = {
    ...user,
    username: user.contact.emailAddress,
    userClass: user.userClass.id,
  };

  if (!user.primaryRetailer && user.userClass.type === 'INDEPENDENT') {
    queryParams.primaryRetailer = { retailerDisplayNumber };
  }
  const urlParams = { locale };

  return DepsService.restSrv.create(USER_CONTEXT, url, queryParams, urlParams);
};

export const fetchRetailerDetails = (retailerId) => {
  const { url } = DepsService.rest.user.retailerSettings.selfRegistration;
  const queryParams = {};
  const urlParams = {
    retailerId,
  };

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams);
};

export const fetchRetailerDetailsForSecondary = (retailerId, isSecondary) => {
  const { url } = DepsService.rest.user.retailerSettings.secondaryValidation;
  const queryParams = {};
  const urlParams = {
    retailerId,
    isSecondary,
  };

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams);
};

export const fetchUsersReportingTo = (userId) => {
  const { url } = DepsService.rest.user.reportTo;
  const queryParams = {};
  const urlParams = {
    userId,
  };

  return DepsService.restSrv.list(USER_CONTEXT, url, queryParams, urlParams);
};



// WEBPACK FOOTER //
// ./src/modules/user/resources/user.api.js